import { useState } from 'react';
import { Link, generatePath, useLocation, useNavigate, useRevalidator } from 'react-router';
import { pushSubscriptionModifyEventToDataLayer } from '../../common/analytics';
import { EIP_EXCHANGE_ADDON_ID } from '../../common/constants/commonConstants';
import { dsClass } from '../../common/constants/dsClasses';
import { paths } from '../../common/constants/pathVariables';
import { DialogType } from '../../common/enums';
import { changeSubscriptionMobilePbx } from '../../common/fetch.js';
import { useSearchParams } from '../../common/hooks/useSearchParams.js';
import {
  editSettingsMsg,
  extensionNumberMsg,
  instructionsForServiceMsg,
  instructionsMsg,
  monthlyFeeOfSubAfterChangesMsg,
  ringServiceSettingsMsg,
  seeMsg,
  somethingWentWrongMsg,
  t,
} from '../../common/i18n';
import { EMAIL_REGEX } from '../../common/utils/emailUtils';
import { formatPhoneNumber } from '../../common/utils/phoneNumberUtils';
import { centsToEuros, formatNumberToCurrency } from '../../common/utils/priceUtils';
import {
  getAddonAssociationsAndDependencies,
  getConfiguredPbxSolution,
  getSubscriptionUrlId,
} from '../../common/utils/subscriptionUtils';
import { SubscriptionStatusType } from '../../generated/api/subscriptionStatusType';
import { openDialog } from '../../public/site/dialogStore.js';
import { addNotification } from '../../public/site/notificationStore.js';
import { DetailsWithCard } from '../DetailsWithInfoColumn/DetailsWithInfoColumn';
import { HeaderWithEditButton } from '../HeaderWithButton/HeaderWithButton';
import { Loading } from '../Loading';
import { RingSettingsContent } from './RingSettingsContent';
import type { DialogParams } from '../../common/types/dialog';
import type { AddOn } from '../../generated/api/addOn';
import type { AddOnRulesResponse } from '../../generated/api/addOnRulesResponse';
import type { CompanyInfoResponse } from '../../generated/api/companyInfoResponse';
import type { Contact } from '../../generated/api/contact';
import type { OnlineModel } from '../../generated/api/onlineModel';
import type { Subscription } from '../../generated/api/subscription';
import type { SubscriptionPbxConfiguration } from '../../generated/api/subscriptionPbxConfiguration';

export interface VoiceSubscriptionRingSettingsProps {
  subscription: Subscription;
  hasPendingActions: boolean;
  companyInfo?: CompanyInfoResponse;
  contacts?: Contact[];
  ringModel: OnlineModel;
  addOnRules?: AddOnRulesResponse;
  addOnRulesMobilePbx?: AddOnRulesResponse;
}

export const VoiceSubscriptionRingSettings = ({
  subscription,
  hasPendingActions,
  companyInfo,
  contacts,
  ringModel,
  addOnRules,
  addOnRulesMobilePbx,
}: VoiceSubscriptionRingSettingsProps) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { search } = useLocation();
  const { mdmId } = useSearchParams<{ mdmId?: string }>();
  const { revalidate } = useRevalidator();
  const { associations, dependencies } = getAddonAssociationsAndDependencies(
    subscription,
    addOnRules,
    addOnRulesMobilePbx
  );
  const configuredPbxSolution = getConfiguredPbxSolution(subscription, companyInfo?.pbxSolutions ?? [])!;
  const onShowDialog = (params: DialogParams) => openDialog(params);
  const onEditIfNoActionsPending = () => {
    if (subscription?.subscriptionStatus === SubscriptionStatusType.IN_ACTIVATION) {
      onShowDialog({ type: DialogType.MACD_FORBIDDEN_SUBSCRIPTION_IN_ACTIVATION });
      return false;
    }
    if (hasPendingActions) {
      onShowDialog({ type: DialogType.SUBSCRIPTION_ACTION_PENDING });
      return false;
    }
    setEditing(true);
    return true;
  };
  const subscriptionPbxConfiguration = subscription.details!.mobile
    ? subscription.details!.mobile!.pbxConfiguration
    : subscription.details!.mobilePbxEndUserService;

  if (!ringModel || !associations || !dependencies || !subscriptionPbxConfiguration) {
    return (
      <div className={dsClass.PADDING_BOTTOM_4}>
        <Loading />
      </div>
    );
  }
  const ringUserName = subscriptionPbxConfiguration!.pbxConfigurationDetails.ringUserName;
  const subscriptionContact = contacts?.find(
    ({ contactId, person }) => person && contactId === subscription.subscriptionContactId
  );
  const ringUserNames: string[] = [];
  if (ringUserName) {
    ringUserNames.push(ringUserName);
  }
  if (subscriptionContact) {
    const contactEmail = subscriptionContact.person?.email;
    if (contactEmail && !ringUserNames.includes(contactEmail)) {
      ringUserNames.push(subscriptionContact.person!.email);
    }
  }

  const saveRingSettings = async (pbxConfig: SubscriptionPbxConfiguration) => {
    setSaving(true);
    try {
      await changeSubscriptionMobilePbx(pbxConfig, subscription.subscriptionId, mdmId);
      setEditing(false);
      addNotification(t.T29V('Change order successful'), 'success');
      navigate(
        generatePath(paths.PS_MOBILE_SUBSCRIPTION, { subscriptionId: getSubscriptionUrlId(subscription) }) + search
      );
      revalidate();
    } catch {
      addNotification(t.JDTO(somethingWentWrongMsg), 'error');
    } finally {
      setSaving(false);
    }
  };

  const onShowOverviewDialog = (ringModelAddOnsFound: AddOn[], pbxConfig: SubscriptionPbxConfiguration) => {
    const totalAddonsPriceMonthly: number = ringModelAddOnsFound
      .map(addon => addon.addOnMonthlyRecurringCharge || 0)
      .reduce((sum: number, val: number) => sum + val, 0);
    const totalSubscriptionPriceMonthly = subscription?.details?.monthlyRecurringListCharge || 0;
    const totalPriceMonthly = totalAddonsPriceMonthly + totalSubscriptionPriceMonthly;
    const corporateNumber = subscription.details?.mobile?.pbxConfiguration?.corporateNumber;
    onShowDialog({
      body: (
        <div className="of-pbx-ring-settings-accordion">
          <div className={`row ${dsClass.MARGIN_TOP_6}`}>
            <div className="fifty">{t.D9TG('Corporate number')}</div>
            <div className="fifty">{t.NO66(extensionNumberMsg)}</div>
          </div>
          <div className={`row ${dsClass.MARGIN_BOTTOM_4}`}>
            <div className="fifty">{corporateNumber && formatPhoneNumber(corporateNumber, true)}</div>
            <div className="fifty">{subscription.details?.mobile?.pbxConfiguration?.extensionNumber}</div>
          </div>

          {ringModelAddOnsFound?.map((addOn, i) => {
            const price = centsToEuros(addOn.addOnMonthlyRecurringCharge || addOn.addOnOneTimeCharge || 0);
            const suffix = ` €${addOn.addOnMonthlyRecurringCharge ? ' /' + t.XXVX('month') : ''}`;
            return (
              <div
                className={`row${i === ringModelAddOnsFound.length - 1 ? ' dsClass.MARGIN_BOTTOM_4' : ''}`}
                key={addOn.addOnCode}
              >
                <div className="seventy-five">{addOn.addOnProductName}</div>
                <div className="twenty-five">
                  {formatNumberToCurrency(price)}
                  {suffix}
                </div>
              </div>
            );
          })}
          <div className="row">
            {t.CL9O(monthlyFeeOfSubAfterChangesMsg, `${formatNumberToCurrency(centsToEuros(totalPriceMonthly))} €`)}
          </div>
        </div>
      ),
      header: t.JU94('Ring add-ons'),
      onConfirm: async (onCloseDialog: () => void) => {
        pbxConfig.pbxConfigurationDetails.ringAdditionalServiceCodes = ringModelAddOnsFound.map(
          addon => addon.addOnCode
        );
        onCloseDialog();
        await saveRingSettings(pbxConfig);
      },
      type: DialogType.GENERIC_CONFIRMATION_DIALOG,
    });
  };

  const findAddons = (pbxConfig: SubscriptionPbxConfiguration, ringOnlineModels?: OnlineModel[]) => {
    const selectedServiceLevelCode = pbxConfig.pbxConfigurationDetails.ringServiceLevelCode;

    const selectedOffer = ringOnlineModels?.[0].offers.filter(
      offer => offer.commercialProducts[0].commercialProductCode === selectedServiceLevelCode
    );

    const addonIdsForSelectedOffer =
      selectedOffer?.[0].commercialProducts?.[0].addOnAssociations?.map(assoc => assoc.addOnCode) || [];

    const validRingAdditionalServiceCodes =
      pbxConfig.pbxConfigurationDetails?.ringAdditionalServiceCodes?.filter(addon =>
        addonIdsForSelectedOffer.includes(addon)
      ) || [];

    return (
      (ringOnlineModels &&
        ringOnlineModels[0].addOns?.filter(addOn => validRingAdditionalServiceCodes?.includes(addOn.addOnCode))) ||
      []
    );
  };

  const onSaved = (pbxConfig: SubscriptionPbxConfiguration) => {
    pushSubscriptionModifyEventToDataLayer('ring settings');
    const ringModelAddOns: AddOn[] = findAddons(pbxConfig, ringModel ? [ringModel] : []);
    onShowOverviewDialog(ringModelAddOns, pbxConfig);
  };

  const onSave = (pbxConfig: SubscriptionPbxConfiguration) => {
    const currentRingUserName = pbxConfig.pbxConfigurationDetails.ringUserName;
    const isGivenEmailValid = currentRingUserName && EMAIL_REGEX.test(currentRingUserName);
    if (
      !isGivenEmailValid &&
      pbxConfig.pbxConfigurationDetails?.ringAdditionalServiceCodes?.includes(EIP_EXCHANGE_ADDON_ID)
    ) {
      onShowDialog({
        type: DialogType.NEW_RING_USERNAME,
        onConfirm: (newRingUsername: string) => {
          // Update Ring username to the one user entered
          if (newRingUsername !== '') {
            pbxConfig.pbxConfigurationDetails.ringUserName = newRingUsername;
          }
          onSaved(pbxConfig);
        },
      });
    } else {
      onSaved(pbxConfig);
    }
  };

  return (
    <DetailsWithCard
      cardContent={
        <>
          <h3>{t.RD4R(instructionsMsg)}</h3>
          <p>
            {t.LJ10(seeMsg)}
            <Link to={paths.INSTRUCTIONS_RING} target="_blank" rel="noopener noreferrer">
              {t.Z72R(instructionsMsg.toLowerCase())}
            </Link>
            {t.U63I(instructionsForServiceMsg)}
          </p>
        </>
      }
    >
      <HeaderWithEditButton
        title={t.ALD4(ringServiceSettingsMsg)}
        showButton={!editing}
        buttonText={t.YBJF(editSettingsMsg)}
        onClick={onEditIfNoActionsPending}
        disabled={saving}
      />
      <RingSettingsContent
        companyInfo={companyInfo}
        subscriptionPbxConfiguration={subscriptionPbxConfiguration}
        pbxSolution={configuredPbxSolution!}
        subscriptionId={subscription.subscriptionId}
        onEdit={onEditIfNoActionsPending}
        editing={editing}
        onCancel={() => setEditing(false)}
        onSave={onSave}
        saving={saving}
        ringUserNames={ringUserNames}
        addOnAssociations={associations}
        addOnDependencies={dependencies}
        ringModels={ringModel ? [ringModel] : []}
        subscription={subscription}
        onShowDialog={onShowDialog}
        newLayout={true}
      />
    </DetailsWithCard>
  );
};
